/**
 * Mood State Definitions for Avatar - Research-Based Psychology
 * Maps sentiment/emotion to facial expressions and body language
 * Uses ARKit blendshapes for facial control
 *
 * Based on Facial Action Coding System (FACS) and emotional psychology:
 * - Genuine emotions have specific muscle patterns
 * - Asymmetry signals confusion/skepticism
 * - Inner brow raises = sadness/empathy
 * - Cheek raises = genuine smile (Duchenne)
 */

import type { Sentiment } from '../utils/sentimentAnalyzer';

export interface MoodState {
  name: Sentiment;
  description: string;
  blendshapes: {
    [key: string]: number;
  };
  bodyLanguage?: {
    headTilt?: number;    // X-axis rotation
    headTurn?: number;    // Y-axis rotation
    eyeGaze?: {
      x: number;
      y: number;
    };
  };
}

/**
 * Mood state definitions with blendshape values
 * Values are 0-1 range for morph target influences
 */
export const MOOD_STATES: Record<Sentiment, MoodState> = {

  neutral: {
    name: 'neutral',
    description: 'Calm, attentive, professional demeanor',
    blendshapes: {
      // Minimal expression - attentive but neutral
      browInnerUp: 0.05,
      eyeWide_L: 0.03,
      eyeWide_R: 0.03,
      mouthSmile_L: 0.02,
      mouthSmile_R: 0.02
    },
    bodyLanguage: {
      headTilt: 0,
      headTurn: 0,
      eyeGaze: { x: 0, y: 0 }
    }
  },

  positive: {
    name: 'positive',
    description: 'Warm, friendly, encouraging expression',
    blendshapes: {
      // Genuine smile with raised cheeks — warm without baring teeth.
      // Aug 16 2026: dialled back from 0.60 smile / 0.35 brows / 0.28 wide
      // eyes. The server AI-text sentiment returns `positive` after most
      // cooperative user answers, and personality per-mood scales can push
      // these weights ×1.27 (warm-bubbly 1.1 × positive 1.15), so 0.60
      // rendered as a ~0.76 toothy grin with startled brows — Keith's
      // "full teeth in an extreme way / dark bits on the teeth" Visual IVR
      // report. 0.45 stays below the teeth-baring band even fully scaled
      // (0.45 × 1.27 ≈ 0.57). Big expressive joy belongs to `celebration`,
      // which real emphatic sentiment still reaches.
      mouthSmile_L: 0.45,
      mouthSmile_R: 0.45,
      cheekSquint_L: 0.30,
      cheekSquint_R: 0.30,
      mouthDimple_L: 0.26,
      mouthDimple_R: 0.26,
      // Softly raised, relaxed eyebrows
      browInnerUp: 0.22,
      browOuterUp_L: 0.18,
      browOuterUp_R: 0.18,
      // Slightly widened eyes (interest and warmth, not surprise)
      eyeWide_L: 0.12,
      eyeWide_R: 0.12
    },
    bodyLanguage: {
      headTilt: 0.02,  // Slight head tilt (warmth)
      headTurn: 0,
      eyeGaze: { x: 0, y: 0.02 } // Direct, friendly gaze
    }
  },

  concerned: {
    name: 'concerned',
    description: 'Serious, attentive, problem-solving focus',
    blendshapes: {
      // Furrowed brow (concentration/concern) - VERY STRONG
      browDown_L: 0.65,
      browDown_R: 0.65,
      browInnerUp: 0.45,  // Inner brow up (concern)
      // Pressed lips
      mouthPress_L: 0.35,
      mouthPress_R: 0.35,
      mouthClose: 0.40,
      // Focused, intense eyes
      eyeSquint_L: 0.35,
      eyeSquint_R: 0.35,
      // Mouth downturn - VISIBLE
      mouthFrown_L: 0.35,
      mouthFrown_R: 0.35
    },
    bodyLanguage: {
      headTilt: 0,
      headTurn: 0,
      eyeGaze: { x: 0, y: 0.03 } // Focused, direct gaze
    }
  },

  confused: {
    name: 'confused',
    description: 'Puzzled, seeking clarification - EXTREME asymmetric expression',
    blendshapes: {
      // EXTREME asymmetric brow (classic confusion - one way up, one way down)
      browInnerUp: 0.80,         // VERY HIGH (questioning)
      browOuterUp_L: 0.65,       // LEFT WAY UP (questioning)
      browOuterUp_R: 0.15,       // RIGHT DOWN (skeptical)
      browDown_L: 0.10,          // Left relaxed
      browDown_R: 0.60,          // RIGHT WAY DOWN - INCREASED asymmetry
      // MORE pursed lips (puzzled "huh?")
      mouthPucker: 0.50,         // INCREASED - more pronounced
      mouthFunnel: 0.30,         // INCREASED
      // Squinted eyes (concentrating, trying to understand)
      eyeSquint_L: 0.50,         // INCREASED
      eyeSquint_R: 0.45,         // INCREASED
      // Slight asymmetry in mouth too
      mouthLeft: 0.10            // ADDED - pulls mouth to side (puzzled)
    },
    bodyLanguage: {
      headTilt: 0.10,  // INCREASED - more pronounced "huh?" gesture
      headTurn: -0.03,
      eyeGaze: { x: -0.05, y: 0.05 } // Looking up-left (thinking/searching)
    }
  },

  empathetic: {
    name: 'empathetic',
    description: 'Understanding, compassionate, supportive - "I feel for you"',
    blendshapes: {
      // STRONG "sad eyebrows" (empathy = sadness for others + warmth)
      browInnerUp: 0.40,         // DOUBLED - creates caring/sad angle
      browOuterUp_L: 0.12,       // INCREASED
      browOuterUp_R: 0.12,       // INCREASED
      // Bittersweet expression (smile + slight sadness)
      mouthSmile_L: 0.12,        // Slight warm smile
      mouthSmile_R: 0.12,
      mouthFrown_L: 0.15,        // ADDED - creates bittersweet look
      mouthFrown_R: 0.15,
      // Soft, concerned eyes
      eyeSquint_L: 0.12,         // DOUBLED - shows concern
      eyeSquint_R: 0.12,
      eyeWide_L: 0.08,           // Slightly open (engaged)
      eyeWide_R: 0.08,
      // Gentle smile muscles
      mouthDimple_L: 0.10,
      mouthDimple_R: 0.10,
      cheekSquint_L: 0.10,
      cheekSquint_R: 0.10
    },
    bodyLanguage: {
      headTilt: 0.06,  // INCREASED - more pronounced sympathetic tilt
      headTurn: 0,
      eyeGaze: { x: 0, y: 0 } // Direct, caring gaze
    }
  },

  negative: {
    name: 'negative',
    description: 'Apologetic, regretful, acknowledging bad news - STRONG sad expression',
    blendshapes: {
      // VERY STRONG "sad face" (regret/apology)
      browInnerUp: 0.50,         // MUCH HIGHER - classic sad eyebrows
      browOuterUp_L: 0.15,       // ADDED - creates sad angle
      browOuterUp_R: 0.15,       // ADDED
      browDown_L: 0.25,          // INCREASED - sad angle
      browDown_R: 0.25,          // INCREASED
      // STRONG frown
      mouthFrown_L: 0.40,        // MUCH STRONGER - visible sadness
      mouthFrown_R: 0.40,
      mouthPress_L: 0.25,        // INCREASED - tension
      mouthPress_R: 0.25,
      // Concerned, apologetic eyes
      eyeSquint_L: 0.20,         // INCREASED - shows concern
      eyeSquint_R: 0.20,
      // Tense mouth
      mouthPucker: 0.15,         // INCREASED
      mouthClose: 0.20,          // INCREASED
      mouthLowerDown_L: 0.15,    // ADDED - pulls mouth down
      mouthLowerDown_R: 0.15
    },
    bodyLanguage: {
      headTilt: -0.04,  // DOUBLED - more submission/acknowledgment
      headTurn: 0,
      eyeGaze: { x: 0, y: -0.04 } // DOUBLED - more downward (apologetic)
    }
  },

  // ============================================================
  // NEW MOOD STATES - Sprint 20.5 Enhanced Emotion Support
  // ============================================================

  celebration: {
    name: 'celebration',
    description: 'Joy, excitement, celebrating success - BIG smile with raised eyebrows',
    blendshapes: {
      // Open, toothy grin. Two forces bound this pose. Geometry prefers a
      // WIDER smile: widening pulls the corners back and brings the proud gum
      // ridge forward, so the dark-defect signal falls as the smile widens
      // (measured on the gum-forward heads, and again in the Aug 16 2026
      // regression sweep: /tmp/smile-regressions). Aesthetics bound it the
      // other way: Keith reported the 0.90 / 0.35 / upperUp 0.28 celebration
      // "exposes a lot of the top gums" and "looks awkward" on production.
      // The sweep measured exposed-gum height above the crowns at 1.8-3.0mm
      // under 0.90/0.35/0.28; this pose roughly halves it (Ryan 2.18->1.39mm,
      // Arc 1.84->1.27mm) and also halves the corner hole pixels (Ryan
      // 980->490, Arc 3220->2588), at the cost of ~15% more dark band pixels.
      // If the grin must stay at 0.90, drop only mouthUpperUp to 0.12
      // (variant A in the sweep) — but do not raise upperUp back to 0.28
      // and smile to 0.90 together; that is the measured gummy-awkward pose.
      mouthSmile_L: 0.78,
      mouthSmile_R: 0.78,
      cheekSquint_L: 0.36,
      cheekSquint_R: 0.36,
      mouthDimple_L: 0.28,
      mouthDimple_R: 0.28,
      browInnerUp: 0.38,
      browOuterUp_L: 0.34,
      browOuterUp_R: 0.34,
      eyeWide_L: 0.22,
      eyeWide_R: 0.22,
      jawOpen: 0.32,
      // mouthUpperUp is protective against the DARK gum-band defect but it is
      // also the key that bares the top gums — it lifts the lip off the
      // crowns. 0.15 is the measured balance point: gum exposure halves while
      // the dark-band cost stays within ~15% (see /tmp/smile-regressions,
      // FINDINGS.md regression 3).
      mouthUpperUp_L: 0.15,
      mouthUpperUp_R: 0.15,
      mouthLowerDown_L: 0.10,
      mouthLowerDown_R: 0.10,
    },
    bodyLanguage: {
      headTilt: 0.05,  // Slight excited tilt
      headTurn: 0,
      eyeGaze: { x: 0, y: 0.05 } // Looking up slightly (optimistic)
    }
  },

  concentration: {
    name: 'concentration',
    description: 'Deep focus, processing complex information - thinking expression',
    blendshapes: {
      // Slight brow furrow (focus, not anger)
      browDown_L: 0.35,
      browDown_R: 0.35,
      browInnerUp: 0.20,         // Creates "thinking" angle
      // Narrowed eyes (focus)
      eyeSquint_L: 0.40,
      eyeSquint_R: 0.40,
      // Slight tension in lips (concentration)
      mouthPress_L: 0.20,
      mouthPress_R: 0.20,
      mouthPucker: 0.10,
      // Looking slightly off (accessing memory/thinking)
      mouthClose: 0.25
    },
    bodyLanguage: {
      headTilt: 0.02,
      headTurn: -0.02,           // Slight turn (thoughtful)
      eyeGaze: { x: -0.05, y: 0.08 } // Looking up-left (recalling/thinking)
    }
  },

  surprised: {
    name: 'surprised',
    description: 'Unexpected information - raised eyebrows, wide eyes, open mouth',
    blendshapes: {
      // VERY raised eyebrows (classic surprise)
      browInnerUp: 0.80,
      browOuterUp_L: 0.75,
      browOuterUp_R: 0.75,
      // Wide open eyes
      eyeWide_L: 0.70,
      eyeWide_R: 0.70,
      // Open mouth (gasp)
      jawOpen: 0.35,
      mouthFunnel: 0.25,
      // Slight "O" mouth shape
      mouthPucker: 0.15
    },
    bodyLanguage: {
      headTilt: -0.03,           // Slight backward lean
      headTurn: 0,
      eyeGaze: { x: 0, y: 0.02 } // Direct, engaged gaze
    }
  },

  frustrated: {
    name: 'frustrated',
    description: 'Visible frustration - escalation trigger state for empathetic response',
    blendshapes: {
      // Strong furrowed brow (anger/frustration)
      browDown_L: 0.75,
      browDown_R: 0.75,
      browInnerUp: 0.30,         // Tension
      // Tight, tense lips
      mouthPress_L: 0.50,
      mouthPress_R: 0.50,
      mouthClose: 0.40,
      // Slight sneer (disgust/frustration)
      noseSneer_L: 0.25,
      noseSneer_R: 0.25,
      // Narrowed eyes (intensity)
      eyeSquint_L: 0.45,
      eyeSquint_R: 0.45,
      // Tension in jaw
      mouthFrown_L: 0.35,
      mouthFrown_R: 0.35,
      mouthStretch_L: 0.20,
      mouthStretch_R: 0.20
    },
    bodyLanguage: {
      headTilt: 0,               // Direct, no tilt
      headTurn: 0,
      eyeGaze: { x: 0, y: 0 }    // Intense direct gaze
    }
  }
};

/**
 * Get blendshape values for a specific mood
 * @param mood - The mood state
 * @param intensity - Intensity multiplier (0-1)
 * @returns Blendshape values ready to apply
 */
export function getMoodBlendshapes(
  mood: Sentiment,
  intensity: number = 1.0
): { [key: string]: number } {

  const moodState = MOOD_STATES[mood];
  if (!moodState) return {};

  // Apply intensity multiplier
  const blendshapes: { [key: string]: number } = {};
  Object.entries(moodState.blendshapes).forEach(([key, value]) => {
    blendshapes[key] = value * intensity;
  });

  return blendshapes;
}

/**
 * Blend between two mood states
 * Used for smooth transitions
 * @param fromMood - Starting mood
 * @param toMood - Target mood
 * @param progress - Blend progress (0-1)
 * @returns Blended blendshape values
 */
export function blendMoodStates(
  fromMood: Sentiment,
  toMood: Sentiment,
  progress: number
): { [key: string]: number } {

  const fromBlendshapes = MOOD_STATES[fromMood]?.blendshapes || {};
  const toBlendshapes = MOOD_STATES[toMood]?.blendshapes || {};

  // Get all unique blendshape keys
  const allKeys = new Set([
    ...Object.keys(fromBlendshapes),
    ...Object.keys(toBlendshapes)
  ]);

  const blended: { [key: string]: number } = {};

  allKeys.forEach(key => {
    const fromValue = fromBlendshapes[key] || 0;
    const toValue = toBlendshapes[key] || 0;
    blended[key] = fromValue + (toValue - fromValue) * progress;
  });

  return blended;
}

